import { plainToInstance } from 'class-transformer'

export interface MsSqlNotEqualityFilter<T> {
  operator: 'notequality'
  fieldName: string
  value: T
  type: 'string' | 'number' | 'boolean'
  nextSeparator?: string
}

export interface MsSqlEqualityFilter<T> {
  operator: 'equality'
  fieldName: string
  value: T | null | undefined
  type: 'string' | 'number' | 'boolean'
  nextSeparator?: string
}

export interface MsSqlSuperiorityFilter<T> {
  operator: 'superiority'
  fieldName: string
  isStrict: boolean
  value: T
  type: 'string' | 'number' | 'boolean'
  nextSeparator?: string
}

export interface MsSqlInferiorityFilter<T> {
  operator: 'inferiority'
  fieldName: string
  isStrict: boolean
  value: T
  type: 'string' | 'number' | 'boolean'
  nextSeparator?: string
}

export interface MsSqlLikeFilter<T> {
  operator: 'like'
  fieldName: string
  value: T
  type: 'string' | 'number' | 'boolean'
  nextSeparator?: string
}

export interface MsSqlStartsWithFilter<T> {
  operator: 'startswith'
  fieldName: string
  value: T
  type: 'string' | 'number' | 'boolean'
  nextSeparator?: string
}

export interface MsSqlEndsWithFilter<T> {
  operator: 'endswith'
  fieldName: string
  value: T
  type: 'string' | 'number' | 'boolean'
  nextSeparator?: string
}

export interface MsSqlRangeFilter<T> {
  operator: 'range'
  fieldName: string
  min: T
  max: T
  type: 'string' | 'number' | 'boolean'
  nextSeparator?: string
}

export interface MsSqlOrFilter<T> {
  operator: 'or'
  fieldName: string
  value: T
  type: 'string' | 'number' | 'boolean'
  nextSeparator?: string
}

export interface MsSqlExistsFilter {
  operator: 'exists'
  fieldName: string
  type: 'string' | 'number' | 'boolean'
  nextSeparator?: string
}

export interface MsSqlNotExistsFilter {
  operator: 'notexists'
  fieldName: string
  type: 'string' | 'number' | 'boolean'
  nextSeparator?: string
}

export interface MsSqlValuesFilter<T> {
  operator: 'values'
  fieldName: string
  values: T[]
  type: 'string' | 'number' | 'boolean'
  nextSeparator?: string
}

export type MsSqlFilter<T = string | number | boolean> =
  | MsSqlEqualityFilter<T>
  | MsSqlNotEqualityFilter<T>
  | MsSqlSuperiorityFilter<T>
  | MsSqlInferiorityFilter<T>
  | MsSqlLikeFilter<T>
  | MsSqlStartsWithFilter<T>
  | MsSqlEndsWithFilter<T>
  | MsSqlRangeFilter<T>
  | MsSqlOrFilter<T>
  | MsSqlValuesFilter<T>
  | MsSqlExistsFilter
  | MsSqlNotExistsFilter

export class MsSqlFieldSelected {
  required: string[] | undefined
  removed: string[] | undefined
}

export class MsSqlQueryOptionsPagination {
  static firstResult: MsSqlQueryOptionsPagination = MsSqlQueryOptionsPagination.build({ page: 1, length: 1 })

  static build(raw: any, defaultPageSize: number = 20): MsSqlQueryOptionsPagination & { page: number; length: number } {
    const result: MsSqlQueryOptionsPagination = plainToInstance(MsSqlQueryOptionsPagination, raw)
    return Object.assign(result, { page: result.page || 1, length: result.length || defaultPageSize })
  }

  /**
   * Builds a pagination object from raw HTTP query params (`page`/`pageLength`/`noPagination`),
   * or `undefined` when the caller explicitly asked for every row. Centralizes the
   * `noPagination === true ? null : MsSqlQueryOptionsPagination.build(...)` ternary that used to
   * be copy-pasted in every controller.
   */
  static fromQueryParams(
    queryParams: { page?: number; pageLength?: number; noPagination?: boolean },
    defaultPageSize: number = 20,
  ): MsSqlQueryOptionsPagination | undefined {
    if (queryParams.noPagination === true) {
      return undefined
    }
    return MsSqlQueryOptionsPagination.build(
      { page: queryParams.page, length: queryParams.pageLength },
      defaultPageSize,
    )
  }

  length: number | undefined
  page: number | undefined
  // Historically set as either a number (e.g. `1`) or a boolean depending on the call site -
  // every reader only ever does a truthy check, so both are accepted rather than picking one.
  noPagination: number | boolean | undefined
}

export interface MsSqlQueryOptions {
  filters?: MsSqlFilter[]
  fields?: MsSqlFieldSelected
  sort?: { [key: string]: 'asc' | 'desc' }
  pagination?: MsSqlQueryOptionsPagination | null
}
